/**
 * The browser EngineLink: leader election and the RPC routing of web.md §1 / contracts/rpc.ts.
 *
 *   - Every tab asks for the Web Lock `digester-engine`. The holder is the LEADER: it starts the engine
 *     worker (platform/worker.ts) and answers requests of all tabs on `BroadcastChannel('digester-rpc')`.
 *   - The others are FOLLOWERS: their requests go out on the channel; the leader forwards them to its
 *     worker and relays responses and events back on the channel.
 *   - When the leader closes, the lock passes; the next tab starts a worker over the same OPFS file and
 *     broadcasts `leader-ready`; followers' clients retry in-flight calls once (engine-client/client.ts).
 *
 * The link never interprets results; it only routes messages by kind and by request id prefix.
 */

import {
  RPC_CHANNEL, RPC_LOCK, toShape, type RpcHello, type RpcHostError, type RpcLeaderReady, type RpcMessage, type RpcRequest,
} from '@digester/core';
import type { EngineLink, LinkInbound } from '../engine-client/link.js';
import { detectOnDeviceSupport } from './model/support.js';
import type { HostToWorker, WorkerInit, WorkerToHost } from './protocol.js';

export interface BrowserLinkOptions {
  /** API origin; default `import.meta.env.VITE_API_BASE` or '' (same origin). */
  apiBase?: string;
  telegram?: { apiId: number | null; apiHash: string | null };
  /** Tests: isolate the OPFS pool, force memory, disable timers, use their own lock and channel. */
  poolName?: string;
  forceMemory?: boolean;
  timers?: boolean;
  lockName?: string;
  channelName?: string;
  /** Tests: the OPFS directory of model files, a short idle unload, or no on-device model at all. */
  modelStoreRoot?: string;
  modelIdleUnloadMs?: number;
  onDeviceModels?: boolean;
}

export type LinkRole = 'follower' | 'leader';

export interface BrowserEngineLink extends EngineLink {
  role(): LinkRole;
  /** Leadership changes (for diagnostics and tests). */
  onRole(listener: (role: LinkRole) => void): () => void;
}

function randomId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return `t${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

function envTelegram(): { apiId: number | null; apiHash: string | null } {
  const id = Number(import.meta.env.VITE_TELEGRAM_API_ID ?? '');
  const hash = import.meta.env.VITE_TELEGRAM_API_HASH ?? '';
  return { apiId: Number.isInteger(id) && id > 0 ? id : null, apiHash: hash !== '' ? hash : null };
}

function isRpcMessage(value: unknown): value is RpcMessage {
  return typeof value === 'object' && value !== null && typeof (value as { kind?: unknown }).kind === 'string';
}

export function createBrowserEngineLink(options: BrowserLinkOptions = {}): BrowserEngineLink {
  const tabId = randomId();
  const ownPrefix = `${tabId}:`;
  const channel = new BroadcastChannel(options.channelName ?? RPC_CHANNEL);
  const listeners = new Set<(message: LinkInbound) => void>();
  const roleListeners = new Set<(role: LinkRole) => void>();
  const releaseLock = new AbortController();
  let role: LinkRole = 'follower';
  let worker: Worker | null = null;
  let ready: RpcLeaderReady | null = null;
  let closed = false;
  /** Requests waiting for this tab's worker to finish booting (own and forwarded ones). */
  const waiting: RpcRequest[] = [];

  const deliver = (message: LinkInbound): void => {
    for (const listener of [...listeners]) listener(message);
  };
  const broadcast = (message: RpcMessage): void => {
    if (!closed) channel.postMessage(message);
  };
  const toWorker = (message: HostToWorker): void => worker?.postMessage(message);

  // ── The model worker (models.md §6.2): created lazily when the engine worker asks, connected to it by a
  // MessageChannel, terminated when it says the model is idle. Only the leader has one.
  let modelWorker: Worker | null = null;
  const stopModelWorker = (): void => {
    modelWorker?.terminate();
    modelWorker = null;
  };
  const startModelWorker = (): void => {
    stopModelWorker();
    let w: Worker;
    try {
      w = new Worker(new URL('./model/worker.ts', import.meta.url), { type: 'module', name: `digester-model-${tabId}` });
    } catch (error) {
      toWorker({ kind: 'model-worker-error', message: error instanceof Error ? error.message : String(error) });
      return;
    }
    modelWorker = w;
    w.onerror = (event: ErrorEvent) => {
      event.preventDefault();
      if (modelWorker !== w) return;
      stopModelWorker();
      toWorker({ kind: 'model-worker-error', message: event.message || 'The model worker failed to start or stopped (out of memory?).' });
    };
    const channel = new MessageChannel();
    w.postMessage({ kind: 'port', port: channel.port1 }, [channel.port1]);
    worker?.postMessage({ kind: 'model-port', port: channel.port2 } satisfies HostToWorker, [channel.port2]);
  };

  // The battery, for HostSignals.power (models.md §6.6): Battery Status API where it exists (Chromium).
  const watchBattery = (): void => {
    const nav = globalThis.navigator as (Navigator & { getBattery?: () => Promise<{ charging: boolean; level: number; addEventListener(t: string, l: () => void): void }> }) | undefined;
    if (typeof nav?.getBattery !== 'function') return;
    nav
      .getBattery()
      .then((battery) => {
        const send = (): void => toWorker({ kind: 'battery', charging: battery.charging, level: battery.level });
        send();
        battery.addEventListener('chargingchange', send);
        battery.addEventListener('levelchange', send);
      })
      .catch(() => {
        /* not allowed here (permissions policy, privacy settings): power stays 'normal' */
      });
  };

  const fromWorker = (message: WorkerToHost): void => {
    switch (message.kind) {
      case 'response':
        if (message.id.startsWith(ownPrefix)) deliver(message);
        else broadcast(message);
        return;
      case 'event':
        deliver(message);
        broadcast(message);
        return;
      case 'leader-ready':
        ready = message;
        deliver(message);
        broadcast(message);
        for (const request of waiting.splice(0)) toWorker(request);
        return;
      case 'host-error':
        deliver(message);
        broadcast(message);
        return;
      case 'model-worker':
        if (message.action === 'open') startModelWorker();
        else stopModelWorker();
        return;
    }
  };

  const becomeLeader = (): void => {
    if (closed) return;
    role = 'leader';
    for (const listener of [...roleListeners]) listener(role);
    const w = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: `digester-engine-${tabId}` });
    worker = w;
    w.onmessage = (event: MessageEvent<WorkerToHost>) => fromWorker(event.data);
    w.onerror = (event: ErrorEvent) => {
      event.preventDefault();
      const error: RpcHostError = { kind: 'host-error', error: toShape(new Error(event.message || 'The engine worker failed to start')) };
      deliver(error);
      broadcast(error);
    };
    const support = options.onDeviceModels === false ? { supported: false, reason: 'Turned off for this page.' } : detectOnDeviceSupport();
    if (!support.supported) console.log(`[host] no on-device model in this tab: ${support.reason ?? ''}`);
    const init: WorkerInit = {
      kind: 'init',
      tabId,
      apiBase: options.apiBase ?? import.meta.env.VITE_API_BASE ?? '',
      telegram: options.telegram ?? envTelegram(),
      onDeviceModels: support,
    };
    if (options.poolName !== undefined) init.poolName = options.poolName;
    if (options.forceMemory !== undefined) init.forceMemory = options.forceMemory;
    if (options.timers !== undefined) init.timers = options.timers;
    if (options.modelStoreRoot !== undefined) init.modelStoreRoot = options.modelStoreRoot;
    if (options.modelIdleUnloadMs !== undefined) init.modelIdleUnloadMs = options.modelIdleUnloadMs;
    w.postMessage(init);
    watchBattery();
  };

  channel.onmessage = (event: MessageEvent<unknown>) => {
    const message = event.data;
    if (!isRpcMessage(message) || closed) return;
    if (role === 'leader') {
      if (message.kind === 'request') {
        if (ready) toWorker(message);
        else waiting.push(message);
      } else if (message.kind === 'hello' && ready) {
        broadcast(ready);
      }
      return;
    }
    // Follower: only answers to our own requests, events and leader announcements concern us.
    switch (message.kind) {
      case 'response':
        if (message.id.startsWith(ownPrefix)) deliver(message);
        return;
      case 'event':
      case 'leader-ready':
      case 'host-error':
        deliver(message);
        return;
      default:
        return;
    }
  };

  const signal = (s: 'visible' | 'hidden' | 'focus'): void => {
    if (role === 'leader') toWorker({ kind: 'signal', signal: s });
  };
  const onVisibility = (): void => signal(document.visibilityState === 'hidden' ? 'hidden' : 'visible');
  const onFocus = (): void => signal('focus');
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
    globalThis.addEventListener('focus', onFocus);
  }

  const locks = (globalThis.navigator as Navigator | undefined)?.locks;
  if (locks) {
    locks
      .request(options.lockName ?? RPC_LOCK, { signal: releaseLock.signal }, () => {
        becomeLeader();
        // Held for the life of the tab (or until close()).
        return new Promise<void>((resolve) => releaseLock.signal.addEventListener('abort', () => resolve()));
      })
      .catch(() => {
        /* aborted by close() before the lock was granted */
      });
  } else {
    // No Web Locks (very old browsers): every tab is its own leader; the second one gets storage_unavailable.
    queueMicrotask(becomeLeader);
  }
  const hello: RpcHello = { kind: 'hello', tabId };
  broadcast(hello);

  return {
    tabId,
    send(request) {
      if (closed) return;
      if (role === 'leader') {
        if (ready) toWorker(request);
        else waiting.push(request);
      } else {
        broadcast(request);
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    isLocal: () => role === 'leader',
    role: () => role,
    onRole(listener) {
      roleListeners.add(listener);
      return () => {
        roleListeners.delete(listener);
      };
    },
    close() {
      if (closed) return;
      closed = true;
      releaseLock.abort();
      stopModelWorker();
      worker?.terminate();
      worker = null;
      channel.close();
      listeners.clear();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
        globalThis.removeEventListener('focus', onFocus);
      }
    },
  };
}
